// apps/admin/admin-main/src/pages/UberDeliveries/DeliveryCard.tsx
import React from 'react';
import {
  Card, CardContent, CardActionArea, Typography, Stack, Chip, Box, Divider,
} from '@mui/material';
import {
  LocalShipping as CourierIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { STATUS_COLORS, STATUS_LABELS } from './constants';

export interface UberDelivery {
  id: string;
  status: string;
  tracking_url?: string;
  created: string;
  pickup?: { name?: string; address?: string; eta?: string };
  dropoff?: { name?: string; address?: string; eta?: string };
  courier?: { name?: string; phone_number?: string; vehicle_type?: string };
  fee?: number;
  tip?: number;
  manifest_reference?: string;
  pickup_notes?: string;
  dropoff_notes?: string;
  deeliver_order?: {
    id: string;
    orderNumber: string;
    customerName: string | null;
    status: string;
  } | null;
}

interface Props {
  delivery: UberDelivery;
  expanded: boolean;
  onClick: () => void;
}

export default function DeliveryCard({ delivery, expanded, onClick }: Props) {
  const statusColor = STATUS_COLORS[delivery.status] || '#9e9e9e';
  const statusLabel = STATUS_LABELS[delivery.status] || delivery.status;
  const createdTime = new Date(delivery.created).toLocaleTimeString('en-ZA', {
    hour: '2-digit', minute: '2-digit',
  });
  const createdDate = new Date(delivery.created).toLocaleDateString('en-ZA');

  return (
    <Card sx={{
      border: expanded ? '2px solid' : '1px solid',
      borderColor: expanded ? 'primary.main' : 'divider',
      transition: 'border-color 0.2s',
    }}>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontFamily="monospace" noWrap sx={{ maxWidth: '60%' }}>
              {delivery.deeliver_order?.orderNumber || delivery.id.slice(0, 12)}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 600 }}
            />
          </Stack>

          {delivery.deeliver_order?.customerName && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {delivery.deeliver_order.customerName}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {delivery.dropoff?.address && (
            <Typography variant="caption" color="text.secondary" noWrap display="block" mb={0.5}>
              To: {delivery.dropoff.address}
            </Typography>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
            {delivery.courier?.name ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CourierIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {delivery.courier.name}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.disabled">No courier yet</Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {createdDate} {createdTime}
              </Typography>
            </Stack>
          </Stack>

          {delivery.fee != null && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Fee: R{(delivery.fee / 100).toFixed(2)}
              {delivery.tip ? ` + R${(delivery.tip / 100).toFixed(2)} tip` : ''}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
